import {NgClass} from '@angular/common';
import {Component, signal, WritableSignal} from '@angular/core';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';
import {HomePageContent, InstanceUIConfigResponse, UiConfigService} from '@middleware';
import {HomeToutComponent} from './home-tout.component';

export type SubToutsCSSClass = 'subtouts' | 'subtouts single-tout' | 'subtouts two-touts';
type InstanceUIConfigSchema = InstanceUIConfigResponse['config_data'];

@Component({
  standalone: true,
  selector: 'app-home',
  imports: [HomeToutComponent, NgClass],
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss',
})
export class HomeComponent {
  /**
   * The uiConfigService.getInstanceUIConfig endpoint returns a InstanceUIConfigResponse
   * object that should contain the following content fields:
   *
   * `content`:
   *     `homepage`: Home Page Content
   *        `hero_image`: Hero Image
   *            `src`: Image Location (relative path or URL)
   *            `title`: Title
   *            `alt_text`: Alt Text
   *        `intro`: Introduction
   *            title: Headline (and SEO description)
   *            description: Instance Description HTML
   *        `sections`: Sections
   *            name: Name (`resource_explorer`, `public_commons`, or `private_data_enclave`)
   *            title: Title
   *            icon: Icon Name
   *            description: Section Description HTML
   */
  trustedDescription: SafeHtml;
  homePageContent: WritableSignal<HomePageContent> = signal(undefined);
  configData: WritableSignal<InstanceUIConfigSchema> = signal<InstanceUIConfigSchema | undefined>(undefined);

  constructor(
    private readonly uiConfigService: UiConfigService,
    protected readonly sanitizer: DomSanitizer,
  ) {
    this.uiConfigService.getInstanceUIConfig().subscribe((config: InstanceUIConfigResponse) => {
      this.homePageContent.set(config?.config_data?.content?.homepage);
      this.configData.set(config?.config_data);

      this.trustedDescription = sanitizer.bypassSecurityTrustHtml(
        config?.config_data?.content?.homepage.intro.description,
      ); // nosonar
    });
  }

  /**
   * Determines and returns the CSS class for subtouts based on the active sections count.
   *
   * Note: if a subtout's section content (from the instance UI config) is not defined,
   * the class set here doesn't care, because if the corresponding module (e.g. Resource Explorer) is
   * enabled, it is expected for the subtout section content to be set.
   * Having the layout obviously wrong will provide a very visible reminder to set that content.
   *
   * @return {SubToutsCSSClass}
   */
  getSubtoutsClass(): SubToutsCSSClass {
    switch (this.getActiveSectionsCount()) {
      case 2:
        return 'subtouts two-touts';
      case 1:
        return 'subtouts single-tout';
      default:
        return 'subtouts';
    }
  }

  /**
   * Calculates and returns the count of active sections based on the enabled status
   * in the configuration data.
   *
   *
   * @return {number} The number of enabled modules (e.g. Resource Explorer)
   */
  getActiveSectionsCount(): number {
    let counter = 0;
    if (this.configData() === undefined || this.homePageContent() === undefined) {
      return counter;
    }

    if (this.configData()?.resource_explorer.enabled) {
      counter++;
    }

    if (this.configData()?.private_data_enclave.enabled) {
      counter++;
    }

    if (this.configData()?.public_data_commons.enabled) {
      counter++;
    }

    return counter;
  }
}
